/**
 * Funder SSOT for home-care / aged-care clients.
 *
 * A client's funder(s) are recorded in `clients.funding_programs` (text[], NOT NULL).
 * This module is the single source of truth for deriving "which funder logic
 * applies" from that array, so the SAH money machinery (budget provisioning,
 * contribution, statements, claims, crons) can be gated consistently — and
 * non-SAH clients (NDIS, private, CHSP, DVA) onboard cleanly without ever
 * triggering SAH funding logic.
 *
 * Design note: we deliberately derive the funder from the existing
 * `funding_programs` array rather than introducing a second scalar
 * `funding_source` column. A duplicate funder field is exactly the
 * vocabulary-mismatch defect class this codebase actively fights — one
 * NOT-NULL array + one deterministic helper = one source of truth, zero drift.
 * The canonical values match `clients.funding_programs` / `care_plans.plan_type`
 * vocab (the funding_programs CHECK constraint forbids the 'SAH'/'AN-ACC'
 * string variants, so only these canonical tokens are valid).
 */

export type Funder =
  | 'SUPPORT_AT_HOME'
  | 'HCP'
  | 'CHSP'
  | 'NATSIFACP'
  | 'DVA'
  | 'NDIS'
  | 'TAC'
  | 'PRIVATE'
  | 'AN_ACC'
  | 'RAC_RESPITE';

export const FUNDERS: readonly Funder[] = [
  'SUPPORT_AT_HOME',
  'HCP',
  'CHSP',
  'NATSIFACP',
  'DVA',
  'NDIS',
  'TAC',
  'PRIVATE',
  'AN_ACC',
  'RAC_RESPITE',
];

/**
 * Priority order for resolving a single "primary" funder when a client carries
 * multiple programs. The most-regulated government funder ranks highest so it
 * drives the onboarding journey (e.g. a SAH + DVA client is treated as SAH).
 */
const FUNDER_PRIORITY: readonly Funder[] = [
  'SUPPORT_AT_HOME',
  'HCP',
  'NDIS',
  'AN_ACC',
  'RAC_RESPITE',
  'CHSP',
  'NATSIFACP',
  'DVA',
  'TAC',
  'PRIVATE',
];

export const FUNDER_LABELS: Record<Funder, string> = {
  SUPPORT_AT_HOME: 'Support at Home',
  HCP: 'Home Care Package',
  CHSP: 'CHSP',
  NATSIFACP: 'NATSIFAC Programme',
  DVA: 'DVA',
  NDIS: 'NDIS',
  TAC: 'TAC',
  PRIVATE: 'Private',
  AN_ACC: 'Residential (AN-ACC)',
  RAC_RESPITE: 'Residential respite (RAC)',
};

function normalizeFunderToken(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');
}

/**
 * STRICT type guard: is `value` EXACTLY a canonical funder token? Use this for
 * WRITE-side validation. It deliberately does NOT normalize — accepting messy
 * variants (e.g. 'support at home', 'AN-ACC') on the write path would
 * reintroduce the vocabulary drift this SSOT exists to prevent, and the
 * `funding_programs` CHECK constraint forbids those variants anyway.
 */
export function isFunder(value: unknown): value is Funder {
  return typeof value === 'string' && (FUNDERS as readonly string[]).includes(value);
}

/**
 * Tolerant READ-side parse: normalize a possibly-messy token to its canonical
 * `Funder`, or `null` if unrecognised. Use this when interpreting external /
 * legacy data, never to validate a write.
 */
export function toFunder(value: unknown): Funder | null {
  const token = normalizeFunderToken(value);
  return (FUNDERS as readonly string[]).includes(token) ? (token as Funder) : null;
}

/** The known funders present on a client's `funding_programs`, in canonical form. */
export function getFunders(fundingPrograms: readonly string[] | null | undefined): Funder[] {
  if (!Array.isArray(fundingPrograms)) return [];
  const seen = new Set<Funder>();
  for (const raw of fundingPrograms) {
    const funder = toFunder(raw);
    if (funder) seen.add(funder);
  }
  return FUNDER_PRIORITY.filter((f) => seen.has(f));
}

/**
 * The single primary funder for a client, by regulatory priority, or `null`
 * when none of the programs are recognised funders.
 */
export function getPrimaryFunder(
  fundingPrograms: readonly string[] | null | undefined,
): Funder | null {
  const funders = getFunders(fundingPrograms);
  return funders[0] ?? null;
}

/**
 * Whether SAH funding logic applies. This is the canonical gate for the SAH
 * money machinery (classification, budget provisioning, HCP transition,
 * contribution, statements, claims). Transitioned Home Care Package clients can
 * carry either `SUPPORT_AT_HOME` or the legacy `HCP` token while their evidence
 * is being reconciled. Other co-funders, including CHSP for separately scoped
 * services, do not suppress an explicit SAH/HCP program.
 */
export function isSahFunded(fundingPrograms: readonly string[] | null | undefined): boolean {
  const funders = getFunders(fundingPrograms);
  return funders.includes('SUPPORT_AT_HOME') || funders.includes('HCP');
}

/**
 * Whether the SAH client surfaces (panels, plan spine, budget) should be shown
 * for a client. This is the UI funder gate used by the client-record hub and the
 * `/clients/[id]/sah` route: SAH is shown when the client is SAH-funded OR has NO
 * recorded funder at all (fail-OPEN for legacy rows whose `funding_programs` was
 * never backfilled — do not strand an existing SAH client). An NDIS/private/
 * residential client WITH a recorded non-SAH funder is gated out.
 */
export function isSahFundedClient(fundingPrograms: readonly string[] | null | undefined): boolean {
  const arr = fundingPrograms ?? [];
  return arr.length === 0 || isSahFunded(arr);
}

/** Whether the client is NDIS-funded (operational-only — no SAH machinery). */
export function isNdisFunded(fundingPrograms: readonly string[] | null | undefined): boolean {
  return getFunders(fundingPrograms).includes('NDIS');
}

/** Whether the client is privately funded (self-funded — no government funding logic). */
export function isPrivatelyFunded(fundingPrograms: readonly string[] | null | undefined): boolean {
  return getFunders(fundingPrograms).includes('PRIVATE');
}

/** Human-readable label for a funder token (canonical or messy input). */
export function funderLabel(value: unknown): string {
  const funder = toFunder(value);
  if (funder) return FUNDER_LABELS[funder];
  return String(value ?? '').trim() || 'Unknown';
}

/**
 * A support category may be managed a single way for the whole plan
 * (`SELF_MANAGED` / `PLAN_MANAGED` / `AGENCY_MANAGED`). These three are the only
 * values a *category* can take, and therefore the only values
 * `ndis_plan_management.default_management_kind` and
 * `ndis_plan_management_category_overrides.management_kind` may hold.
 *
 * Mirrors `ndis_plan_management_default_kind_check` (migration 20270530000000).
 */
export type NdisCategoryManagementKind = 'SELF_MANAGED' | 'PLAN_MANAGED' | 'AGENCY_MANAGED';

export const NDIS_CATEGORY_MANAGEMENT_KINDS: readonly NdisCategoryManagementKind[] = [
  'SELF_MANAGED',
  'PLAN_MANAGED',
  'AGENCY_MANAGED',
];

/**
 * NDIS plan-management kinds (mirrors the `ndis_plan_management.management_kind`
 * CHECK). `MIXED` was added by migration 20270530000000 because a real plan can
 * split management by support category — a real Golden Bloom plan has one
 * support category self-managed and everything else plan-managed, and before
 * that migration the arrangement was unrepresentable.
 *
 * `MIXED` is deliberately NOT the previously-dropped `COMBINATION` token: reusing
 * it would invite the assumption that its old (never-populated) semantics
 * returned, and the plan documents say "mixed".
 *
 * A `MIXED` plan always carries a `default_management_kind` and at least one
 * category override; every other kind carries neither. That pairing is enforced
 * by `ndis_plan_management_mixed_default_pairing_check` and by
 * `record_ndis_plan_management_controlled`.
 */
export type NdisManagementKind = NdisCategoryManagementKind | 'MIXED';

export const NDIS_MANAGEMENT_KINDS: readonly NdisManagementKind[] = [
  'SELF_MANAGED',
  'PLAN_MANAGED',
  'AGENCY_MANAGED',
  'MIXED',
];

export const NDIS_MANAGEMENT_KIND_LABELS: Record<NdisManagementKind, string> = {
  SELF_MANAGED: 'Self-managed',
  PLAN_MANAGED: 'Plan-managed',
  AGENCY_MANAGED: 'Agency-managed (NDIA)',
  MIXED: 'Mixed (varies by support category)',
};

export function isNdisManagementKind(value: unknown): value is NdisManagementKind {
  return (NDIS_MANAGEMENT_KINDS as readonly string[]).includes(String(value ?? '').trim());
}

export function isNdisCategoryManagementKind(value: unknown): value is NdisCategoryManagementKind {
  return (NDIS_CATEGORY_MANAGEMENT_KINDS as readonly string[]).includes(String(value ?? '').trim());
}

/** One support category whose management differs from the plan default. */
export type NdisCategoryOverride = {
  supportCategory: string;
  managementKind: NdisCategoryManagementKind;
  note?: string | null;
};

/**
 * Does a plan manager pay for any part of this plan? Mirrors the `is_plan_managed`
 * column computed by `ndis_active_plan_management_by_client`, which
 * `generate_ndis_invoices_controlled` joins on. Keep the two in step: a MIXED plan
 * whose default OR any override is `PLAN_MANAGED` needs a plan manager recorded.
 */
export function ndisRequiresPlanManagerIdentity(input: {
  managementKind: string | null | undefined;
  defaultManagementKind?: string | null;
  categoryOverrides?: readonly { managementKind: string }[] | null;
}): boolean {
  const kind = String(input.managementKind ?? '').trim();
  if (kind === 'PLAN_MANAGED') return true;
  if (kind !== 'MIXED') return false;
  if (String(input.defaultManagementKind ?? '').trim() === 'PLAN_MANAGED') return true;
  return (input.categoryOverrides ?? []).some(
    (override) => String(override.managementKind ?? '').trim() === 'PLAN_MANAGED',
  );
}
